import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    Cell,
} from 'recharts';
import type { DashboardFiltros } from './types';
import type { IContratoLancamentoEmpresaResumo, IDashboardContratoLancamento } from '../../interfaces/IContratoLancamento';
import ContratoLancamentoService from '../../services/ContratoLancamentoService';
import StorageUtils from '../../utils/StorageUtils';

type Props = {
    filtros: DashboardFiltros;
};

type LinhaResumo = IContratoLancamentoEmpresaResumo & {
    alerta: 'OK' | 'Alerta' | 'Crítico';
};

type UsoMensal = {
    mes: string;
    utilizado: number;
    percentual: number;
};

const calcularAlerta = (percentual: number): 'OK' | 'Alerta' | 'Crítico' => {
    if (percentual >= 95) return 'Crítico';
    if (percentual >= 80) return 'Alerta';
    return 'OK';
};

const DashboardContratosLancamentos = ({ filtros }: Props) => {
    const [dados, setDados] = useState<IDashboardContratoLancamento | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [erro, setErro] = useState<string | null>(null);

    const cores = {
        fundoPagina: '#f3f6fb',
        fundoCard: '#ffffff',
        borda: '#e5e7eb',
        sombra: '0 10px 30px rgba(15, 23, 42, 0.08)',
        titulo: '#1f2937',
        subtitulo: '#6b7280',
        azul: '#0f5cc0',
        azulEscuro: '#0b4ea2',
        azulClaro: '#42a5f5',
        azulMuitoClaro: '#eff6ff',
        verde: '#0f9f6e',
        verdeClaro: '#ecfdf3',
        laranja: '#f59e0b',
        laranjaEscuro: '#b45309',
        vermelho: '#dc2626',
        vermelhoClaro: '#fef2f2',
        cinzaBarra: '#e5e7eb',
        textoEscuro: '#111827',
        textoMedio: '#4b5563',
        textoSuave: '#64748b',
        fundoNeutro: '#f8fafc',
    };

    const meses = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    useEffect(() => {
        let ativo = true;

        async function carregar() {
            setCarregando(true);
            setErro(null);

            try {
                const usuario = await StorageUtils.getUsuario();
                if (!usuario?.clienteID) {
                    if (ativo) {
                        setErro('Não foi possível identificar o cliente do usuário logado.');
                        setCarregando(false);
                    }
                    return;
                }

                const resultado = await ContratoLancamentoService.obterDashboard(usuario.clienteID);
                if (ativo) setDados(resultado);
            } catch (e: any) {
                if (ativo) setErro(e?.message ?? 'Erro ao carregar o dashboard de contratos');
            } finally {
                if (ativo) setCarregando(false);
            }
        }

        carregar();

        return () => {
            ativo = false;
        };
    }, []);

    const formatarNumero = (valor: number) => valor.toLocaleString('pt-BR');
    const formatarPercentual = (valor: number) => `${valor.toFixed(1).replace('.', ',')}%`;
    const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

    const anoSelecionadoValido = useMemo(() => {
        if (!filtros?.anoSelecionado || filtros.anoSelecionado === 0) return new Date().getFullYear();
        return filtros.anoSelecionado;
    }, [filtros?.anoSelecionado]);

    const temContrato = !!dados && dados.status !== 'SEM_CONTRATO';

    /** Linha da empresa selecionada no filtro superior, se houver e se a modalidade for EMPRESA. */
    const linhaEmpresaFiltrada = useMemo(() => {
        if (!dados || dados.modalidade !== 'EMPRESA' || !filtros?.empresaSelecionada) return null;
        return dados.porEmpresa.find((item) => item.empresaID === filtros.empresaSelecionada) ?? null;
    }, [dados, filtros?.empresaSelecionada]);

    /** Números dos KPIs — da empresa filtrada, se houver, senão do contrato inteiro. */
    const kpi = useMemo(() => {
        if (!dados) return null;

        if (linhaEmpresaFiltrada) {
            const quantidadeExcedente = Math.max(0, linhaEmpresaFiltrada.quantidadeUtilizada - linhaEmpresaFiltrada.quantidadeContratada);
            const valorExcedenteUnitario = linhaEmpresaFiltrada.valorExcedenteUnitario
                ?? (dados.quantidadeExcedente > 0 ? dados.valorExcedente / dados.quantidadeExcedente : 0);

            return {
                quantidadeContratada: linhaEmpresaFiltrada.quantidadeContratada,
                quantidadeUtilizada: linhaEmpresaFiltrada.quantidadeUtilizada,
                quantidadeRestante: linhaEmpresaFiltrada.quantidadeRestante,
                percentualConsumido: linhaEmpresaFiltrada.percentualConsumido,
                quantidadeExcedente,
                valorExcedente: quantidadeExcedente * valorExcedenteUnitario,
            };
        }

        return {
            quantidadeContratada: dados.quantidadeContratada,
            quantidadeUtilizada: dados.quantidadeUtilizada,
            quantidadeRestante: dados.quantidadeRestante,
            percentualConsumido: dados.percentualConsumido,
            quantidadeExcedente: dados.quantidadeExcedente,
            valorExcedente: dados.valorExcedente,
        };
    }, [dados, linhaEmpresaFiltrada]);

    const usoMensal = useMemo<UsoMensal[]>(() => {
        const mapa: Record<string, number> = {
            Jan: 0, Fev: 0, Mar: 0, Abr: 0, Mai: 0, Jun: 0,
            Jul: 0, Ago: 0, Set: 0, Out: 0, Nov: 0, Dez: 0,
        };

        (dados?.distribuicaoMensal ?? []).forEach((item) => {
            const [anoStr, mesStr] = item.periodoReferencia.split('-');
            if (Number(anoStr) !== anoSelecionadoValido) return;

            const nomeMes = meses[Number(mesStr) - 1];
            if (nomeMes) mapa[nomeMes] += item.quantidadeUtilizada;
        });

        const totalAno = Object.values(mapa).reduce((acc, v) => acc + v, 0);

        return meses.map((mes) => ({
            mes,
            utilizado: mapa[mes] || 0,
            percentual: totalAno > 0 ? (mapa[mes] / totalAno) * 100 : 0,
        }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados, anoSelecionadoValido]);

    const totalUtilizadoAno = useMemo(() => usoMensal.reduce((acc, item) => acc + item.utilizado, 0), [usoMensal]);

    /**
     * Uma linha por empresa (modalidade EMPRESA, ou só a empresa filtrada no topo, se houver),
     * ou uma única linha representando o cliente inteiro (modalidade CLIENTE).
     */
    const resumoLinhas = useMemo<LinhaResumo[]>(() => {
        if (!dados) return [];

        if (dados.modalidade === 'EMPRESA' && dados.porEmpresa.length > 0) {
            const linhas = linhaEmpresaFiltrada ? [linhaEmpresaFiltrada] : dados.porEmpresa;
            return linhas.map((item) => ({
                ...item,
                alerta: calcularAlerta(item.percentualConsumido),
            }));
        }

        return [
            {
                id: dados.contratoLancamentoID ?? 0,
                empresaID: 0,
                empresaNome: 'Contabilidade Interna (todas as empresas)',
                quantidadeContratada: dados.quantidadeContratada,
                quantidadeUtilizada: dados.quantidadeUtilizada,
                quantidadeRestante: dados.quantidadeRestante,
                percentualConsumido: dados.percentualConsumido,
                alerta: calcularAlerta(dados.percentualConsumido),
            },
        ];
    }, [dados, linhaEmpresaFiltrada]);

    const quantidadeAlertas = useMemo(() => resumoLinhas.filter((item) => item.alerta !== 'OK').length, [resumoLinhas]);
    const empresasCriticas = useMemo(() => resumoLinhas.filter((item) => item.alerta === 'Crítico').length, [resumoLinhas]);

    const rankingEmpresas = useMemo(
        () => [...resumoLinhas].sort((a, b) => b.percentualConsumido - a.percentualConsumido),
        [resumoLinhas]
    );

    const maiorConsumo = rankingEmpresas[0];
    const mediaConsumoEmpresas = resumoLinhas.length > 0
        ? resumoLinhas.reduce((acc, item) => acc + item.percentualConsumido, 0) / resumoLinhas.length
        : 0;

    const cardStyle: CSSProperties = {
        background: cores.fundoCard,
        border: `1px solid ${cores.borda}`,
        borderRadius: '20px',
        boxShadow: cores.sombra,
        overflow: 'hidden',
        height: 'auto',
    };

    const cardBodyStyle: CSSProperties = { padding: '20px' };

    const cardHeaderStyle: CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '16px',
    };

    const cardTitleStyle: CSSProperties = {
        fontSize: '17px',
        fontWeight: 800,
        color: cores.titulo,
        margin: 0,
        lineHeight: 1.2,
    };

    const cardSubTitleStyle: CSSProperties = {
        fontSize: '12px',
        color: cores.subtitulo,
        fontWeight: 600,
        marginTop: '4px',
    };

    const tabelaHeaderStyle: CSSProperties = {
        fontSize: '11px',
        fontWeight: 800,
        color: '#64748b',
        background: '#f8fafc',
        borderBottom: `1px solid ${cores.borda}`,
        whiteSpace: 'nowrap',
        paddingTop: '12px',
        paddingBottom: '12px',
    };

    const tabelaCellStyle: CSSProperties = {
        fontSize: '13px',
        color: '#334155',
        verticalAlign: 'middle',
        borderBottom: `1px solid ${cores.borda}`,
        whiteSpace: 'nowrap',
        paddingTop: '14px',
        paddingBottom: '14px',
    };

    const scrollInternoStyle: CSSProperties = {
        maxHeight: '340px',
        overflowY: 'auto',
        overflowX: 'hidden',
        paddingRight: '6px',
    };

    const barraPercentual = (percentual: number, corCustomizada?: string) => {
        let cor = corCustomizada || cores.verde;

        if (!corCustomizada) {
            if (percentual >= 95) cor = cores.vermelho;
            else if (percentual >= 80) cor = cores.laranja;
            else cor = cores.verde;
        }

        return (
            <div style={{ width: '100%', height: '8px', background: cores.cinzaBarra, borderRadius: 999, overflow: 'hidden' }}>
                <div
                    style={{
                        width: `${Math.min(percentual, 100)}%`,
                        height: '100%',
                        background: cor,
                        borderRadius: 999,
                        transition: 'all 0.3s ease',
                    }}
                />
            </div>
        );
    };

    const badgeAlerta = (alerta: 'OK' | 'Alerta' | 'Crítico') => {
        const mapa = {
            Crítico: { fundo: cores.vermelhoClaro, cor: cores.vermelho, texto: 'Crítico' },
            Alerta: { fundo: '#fff4db', cor: cores.laranjaEscuro, texto: 'Alerta' },
            OK: { fundo: '#e8f8ef', cor: cores.verde, texto: 'OK' },
        };
        const item = mapa[alerta];

        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '74px',
                    padding: '6px 10px',
                    borderRadius: '999px',
                    fontSize: '12px',
                    fontWeight: 800,
                    background: item.fundo,
                    color: item.cor,
                }}
            >
                {item.texto}
            </span>
        );
    };

    const renderKpiCard = (
        titulo: string,
        valor: string,
        observacao: string,
        corValor: string,
        barra?: number,
        fundoDestaque?: string
    ) => (
        <div style={cardStyle}>
            <div style={{ ...cardBodyStyle, padding: '18px' }}>
                <div
                    style={{
                        width: 44,
                        height: 44,
                        borderRadius: 14,
                        background: fundoDestaque || cores.azulMuitoClaro,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '14px',
                        border: `1px solid ${cores.borda}`,
                    }}
                >
                    <div style={{ width: 14, height: 14, borderRadius: 999, background: corValor }} />
                </div>

                <div style={{ fontSize: '13px', fontWeight: 700, color: cores.textoMedio, marginBottom: '8px' }}>{titulo}</div>
                <div style={{ fontSize: '24px', fontWeight: 900, color: corValor, lineHeight: 1.1, marginBottom: '8px' }}>{valor}</div>
                <div
                    style={{
                        fontSize: '12px',
                        fontWeight: 700,
                        color: cores.subtitulo,
                        marginBottom: barra !== undefined ? '12px' : 0,
                        minHeight: '34px',
                    }}
                >
                    {observacao}
                </div>

                {barra !== undefined && (
                    <div style={{ width: '100%', height: '7px', background: '#edf2f7', borderRadius: 999, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(barra, 100)}%`, height: '100%', background: corValor, borderRadius: 999 }} />
                    </div>
                )}
            </div>
        </div>
    );

    const blocoResumo = (label: string, valor: string, cor: string, fundo: string) => (
        <div style={{ padding: '14px 16px', borderRadius: '16px', background: fundo, border: `1px solid ${cores.borda}` }}>
            <div style={{ fontSize: '12px', fontWeight: 700, color: cores.textoMedio, marginBottom: '6px' }}>{label}</div>
            <div style={{ fontSize: '20px', fontWeight: 900, color: cor }}>{valor}</div>
        </div>
    );

    if (carregando) {
        return (
            <div style={{ background: cores.fundoPagina, borderRadius: '22px', padding: '24px', textAlign: 'center', color: cores.textoSuave }}>
                Carregando dados do contrato...
            </div>
        );
    }

    if (erro) {
        return (
            <div style={{ background: cores.fundoPagina, borderRadius: '22px', padding: '24px', textAlign: 'center', color: cores.vermelho }}>
                {erro}
            </div>
        );
    }

    if (!temContrato) {
        return (
            <div style={{ background: cores.fundoPagina, borderRadius: '22px', padding: '24px' }}>
                <div style={cardStyle}>
                    <div style={cardBodyStyle}>
                        <span style={{ color: cores.textoSuave, fontSize: '14px' }}>
                            Nenhum contrato de volume de lançamentos cadastrado ainda pra esse cliente. Cadastre um contrato
                            na tela de Clientes, aba "Volumes Contratados".
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{ background: cores.fundoPagina, borderRadius: '22px', padding: '8px' }}>
            <div className='row g-4'>
                <div className='col-sm-6 col-xl-3'>
                    {renderKpiCard(
                        'Total de Lançamentos Contratados',
                        formatarNumero(kpi!.quantidadeContratada),
                        linhaEmpresaFiltrada
                            ? `Empresa: ${linhaEmpresaFiltrada.empresaNome ?? '-'}.`
                            : `Modalidade: ${dados!.modalidade === 'EMPRESA' ? 'Por empresa' : 'Contabilidade interna'}.`,
                        cores.azul,
                        kpi!.percentualConsumido,
                        '#eef5ff'
                    )}
                </div>

                <div className='col-sm-6 col-xl-3'>
                    {renderKpiCard(
                        'Total Utilizado',
                        formatarNumero(kpi!.quantidadeUtilizada),
                        `${formatarPercentual(kpi!.percentualConsumido)} do volume total já consumido.`,
                        cores.vermelho,
                        undefined,
                        '#fff1f2'
                    )}
                </div>

                <div className='col-sm-6 col-xl-3'>
                    {renderKpiCard(
                        'Total Disponível',
                        formatarNumero(kpi!.quantidadeRestante),
                        `${formatarPercentual(Math.max(100 - kpi!.percentualConsumido, 0))} ainda disponível.`,
                        cores.verde,
                        undefined,
                        '#eefcf4'
                    )}
                </div>

                <div className='col-sm-6 col-xl-3'>
                    {renderKpiCard(
                        'Excedente',
                        formatarNumero(kpi!.quantidadeExcedente),
                        kpi!.quantidadeExcedente > 0
                            ? `${formatarMoeda(kpi!.valorExcedente)} a faturar no período.`
                            : 'Nenhum excedente no período.',
                        cores.laranja,
                        undefined,
                        '#fff8eb'
                    )}
                </div>

                <div className='col-xl-8 col-12'>
                    <div className='d-flex flex-column gap-4'>
                        <div style={cardStyle}>
                            <div style={cardBodyStyle}>
                                <div style={cardHeaderStyle}>
                                    <div>
                                        <h3 style={cardTitleStyle}>Quantidade de Lançamentos por Mês ({anoSelecionadoValido})</h3>
                                        <div style={cardSubTitleStyle}>Evolução mensal do volume processado no período selecionado.</div>
                                    </div>
                                </div>

                                <div style={{ width: '100%', height: 320 }}>
                                    <ResponsiveContainer width='100%' height='100%'>
                                        <BarChart data={usoMensal} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                            <CartesianGrid strokeDasharray='3 3' stroke='#eef2f7' vertical={false} />
                                            <XAxis dataKey='mes' tick={{ fontSize: 12, fill: '#64748b', fontWeight: 700 }} axisLine={false} tickLine={false} />
                                            <YAxis
                                                tick={{ fontSize: 12, fill: '#64748b', fontWeight: 700 }}
                                                axisLine={false}
                                                tickLine={false}
                                                tickFormatter={(value) => `${Math.round(value / 1000)}k`}
                                            />
                                            <Tooltip
                                                cursor={{ fill: 'rgba(37, 99, 235, 0.06)' }}
                                                formatter={(value: number) => [formatarNumero(value), 'Qtd. lançamentos']}
                                                labelFormatter={(label) => `Mês: ${label}`}
                                            />
                                            <Bar dataKey='utilizado' radius={[10, 10, 0, 0]}>
                                                {usoMensal.map((_, index) => (
                                                    <Cell key={`bar-${index}`} fill={index % 2 === 0 ? cores.azul : cores.azulClaro} />
                                                ))}
                                            </Bar>
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </div>
                        </div>

                        <div style={cardStyle}>
                            <div style={cardBodyStyle}>
                                <div style={cardHeaderStyle}>
                                    <div>
                                        <h3 style={cardTitleStyle}>Lançamentos por Mês</h3>
                                        <div style={cardSubTitleStyle}>Detalhamento mensal do volume utilizado no ano selecionado.</div>
                                    </div>
                                </div>

                                <div className='table-responsive'>
                                    <table className='table align-middle mb-0'>
                                        <thead>
                                            <tr>
                                                <th style={tabelaHeaderStyle}>Mês</th>
                                                <th style={tabelaHeaderStyle}>Qtd. Utilizada</th>
                                                <th style={tabelaHeaderStyle}>% do Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {usoMensal.map((item) => (
                                                <tr key={item.mes}>
                                                    <td style={tabelaCellStyle}>{item.mes}</td>
                                                    <td style={{ ...tabelaCellStyle, fontWeight: 800 }}>{formatarNumero(item.utilizado)}</td>
                                                    <td style={{ ...tabelaCellStyle, fontWeight: 800 }}>{formatarPercentual(item.percentual)}</td>
                                                </tr>
                                            ))}

                                            <tr>
                                                <td style={{ ...tabelaCellStyle, fontWeight: 900 }}>Total</td>
                                                <td style={{ ...tabelaCellStyle, fontWeight: 900 }}>{formatarNumero(totalUtilizadoAno)}</td>
                                                <td style={{ ...tabelaCellStyle, fontWeight: 900 }}>100,0%</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <div style={cardStyle}>
                            <div style={cardBodyStyle}>
                                <div style={cardHeaderStyle}>
                                    <div>
                                        <h3 style={cardTitleStyle}>
                                            {dados!.modalidade === 'EMPRESA' ? 'Resumo Consolidado por Empresa' : 'Resumo do Contrato'}
                                        </h3>
                                        <div style={cardSubTitleStyle}>Visão única para acompanhamento de consumo, saldo e alerta.</div>
                                    </div>
                                </div>

                                <div className='table-responsive'>
                                    <table className='table align-middle mb-0'>
                                        <thead>
                                            <tr>
                                                <th style={tabelaHeaderStyle}>Empresa</th>
                                                <th style={tabelaHeaderStyle}>Qtd. Contratada</th>
                                                <th style={tabelaHeaderStyle}>Qtd. Utilizada</th>
                                                <th style={tabelaHeaderStyle}>Qtd. Restante</th>
                                                <th style={tabelaHeaderStyle}>% Consumido</th>
                                                <th style={tabelaHeaderStyle}>Alerta</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {resumoLinhas.map((item) => (
                                                <tr key={item.id}>
                                                    <td style={tabelaCellStyle}>{item.empresaNome ?? '-'}</td>
                                                    <td style={{ ...tabelaCellStyle, fontWeight: 800 }}>{formatarNumero(item.quantidadeContratada)}</td>
                                                    <td style={{ ...tabelaCellStyle, fontWeight: 800 }}>{formatarNumero(item.quantidadeUtilizada)}</td>
                                                    <td style={{ ...tabelaCellStyle, fontWeight: 800, color: cores.verde }}>
                                                        {formatarNumero(item.quantidadeRestante)}
                                                    </td>
                                                    <td style={{ ...tabelaCellStyle, minWidth: '180px' }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                            <span style={{ fontWeight: 800 }}>{formatarPercentual(item.percentualConsumido)}</span>
                                                            {barraPercentual(item.percentualConsumido)}
                                                        </div>
                                                    </td>
                                                    <td style={tabelaCellStyle}>{badgeAlerta(item.alerta)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                <div
                                    style={{
                                        marginTop: '18px',
                                        padding: '16px 18px',
                                        borderRadius: '14px',
                                        background: '#f8fafc',
                                        border: `1px solid ${cores.borda}`,
                                        color: cores.textoMedio,
                                        fontSize: '13px',
                                        lineHeight: 1.8,
                                    }}
                                >
                                    <strong style={{ color: cores.textoEscuro }}>Regras aplicadas no cálculo:</strong>
                                    <br />
                                    <strong>Total Utilizado</strong> = soma dos processamentos reais registrados no período do contrato
                                    <br />
                                    <strong>Restante</strong> = contratado - utilizado
                                    <br />
                                    <strong>% Utilizado</strong> = (utilizado / contratado) * 100
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className='col-xl-4 col-12'>
                    <div className='d-flex flex-column gap-4'>
                        <div style={cardStyle}>
                            <div style={cardBodyStyle}>
                                <div style={cardHeaderStyle}>
                                    <div>
                                        <h3 style={cardTitleStyle}>Visão Executiva</h3>
                                        <div style={cardSubTitleStyle}>Indicadores consolidados do contrato.</div>
                                    </div>
                                </div>

                                <div>
                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                                        {blocoResumo('Consumo Geral', formatarPercentual(kpi!.percentualConsumido), cores.azul, '#eef5ff')}
                                        {blocoResumo(
                                            'Disponibilidade',
                                            formatarPercentual(Math.max(100 - kpi!.percentualConsumido, 0)),
                                            cores.verde,
                                            '#eefcf4'
                                        )}
                                        {blocoResumo('Itens Monitorados', `${resumoLinhas.length}`, cores.textoEscuro, '#f8fafc')}
                                        {blocoResumo('Em Alerta', `${quantidadeAlertas}`, cores.laranjaEscuro, '#fff8eb')}
                                    </div>

                                    <div
                                        style={{
                                            padding: '14px 16px',
                                            borderRadius: '16px',
                                            background: '#f8fafc',
                                            border: `1px solid ${cores.borda}`,
                                            marginBottom: '16px',
                                        }}
                                    >
                                        <div style={{ fontSize: '12px', color: cores.textoMedio, fontWeight: 700, marginBottom: '8px' }}>
                                            Consumo consolidado
                                        </div>
                                        {barraPercentual(kpi!.percentualConsumido, cores.azul)}
                                        <div
                                            style={{
                                                marginTop: '10px',
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                fontSize: '12px',
                                                fontWeight: 700,
                                                color: cores.textoSuave,
                                                gap: '12px',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <span>{formatarNumero(kpi!.quantidadeUtilizada)} utilizados</span>
                                            <span>{formatarNumero(kpi!.quantidadeContratada)} contratados</span>
                                        </div>
                                    </div>

                                    <div style={{ padding: '14px 16px', borderRadius: '16px', background: '#f8fafc', border: `1px solid ${cores.borda}` }}>
                                        <div style={{ fontSize: '12px', color: cores.textoMedio, fontWeight: 700, marginBottom: '8px' }}>
                                            Leitura rápida
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                            <div style={{ fontSize: '13px', color: cores.textoMedio }}>
                                                Maior consumo atual: {maiorConsumo?.empresaNome ?? '-'}
                                            </div>
                                            <div style={{ fontSize: '13px', color: cores.textoMedio }}>
                                                Percentual do maior consumo:{' '}
                                                {maiorConsumo ? formatarPercentual(maiorConsumo.percentualConsumido) : '0,0%'}
                                            </div>
                                            <div style={{ fontSize: '13px', color: cores.textoMedio }}>Itens críticos: {empresasCriticas}</div>
                                            <div style={{ fontSize: '13px', color: cores.textoMedio }}>
                                                Média de consumo: {formatarPercentual(mediaConsumoEmpresas)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {dados!.modalidade === 'EMPRESA' && (
                            <div style={cardStyle}>
                                <div style={cardBodyStyle}>
                                    <div style={cardHeaderStyle}>
                                        <div>
                                            <h3 style={cardTitleStyle}>Ranking de Consumo</h3>
                                            <div style={cardSubTitleStyle}>Empresas com maior percentual de utilização.</div>
                                        </div>
                                    </div>

                                    <div style={scrollInternoStyle}>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                            {rankingEmpresas.map((item, index) => (
                                                <div
                                                    key={item.id}
                                                    style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '40px 1fr 78px',
                                                        alignItems: 'center',
                                                        gap: '12px',
                                                        paddingBottom: '14px',
                                                        borderBottom: `1px solid ${cores.borda}`,
                                                    }}
                                                >
                                                    <div
                                                        style={{
                                                            width: '40px',
                                                            height: '40px',
                                                            borderRadius: '14px',
                                                            background: index === 0 ? '#fff4db' : '#f8fafc',
                                                            border: `1px solid ${cores.borda}`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            fontSize: '14px',
                                                            fontWeight: 900,
                                                            color: index === 0 ? cores.laranjaEscuro : cores.textoEscuro,
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        {index + 1}
                                                    </div>

                                                    <div style={{ minWidth: 0 }}>
                                                        <div
                                                            style={{
                                                                fontSize: '14px',
                                                                fontWeight: 800,
                                                                color: cores.textoEscuro,
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {item.empresaNome}
                                                        </div>

                                                        <div
                                                            style={{
                                                                fontSize: '12px',
                                                                color: cores.textoSuave,
                                                                marginTop: '3px',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {formatarNumero(item.quantidadeUtilizada)} de {formatarNumero(item.quantidadeContratada)}
                                                        </div>

                                                        <div style={{ marginTop: '8px' }}>{barraPercentual(item.percentualConsumido)}</div>
                                                    </div>

                                                    <div
                                                        style={{
                                                            textAlign: 'right',
                                                            fontSize: '13px',
                                                            fontWeight: 900,
                                                            color:
                                                                item.percentualConsumido >= 95
                                                                    ? cores.vermelho
                                                                    : item.percentualConsumido >= 80
                                                                        ? cores.laranjaEscuro
                                                                        : cores.verde,
                                                        }}
                                                    >
                                                        {formatarPercentual(item.percentualConsumido)}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div style={cardStyle}>
                            <div style={cardBodyStyle}>
                                <div style={cardHeaderStyle}>
                                    <div>
                                        <h3 style={cardTitleStyle}>Resumo Geral do Contrato</h3>
                                        <div style={cardSubTitleStyle}>Síntese executiva consolidada do período contratual.</div>
                                    </div>
                                </div>

                                <div className='d-flex flex-column gap-3'>
                                    <div style={{ padding: '18px', borderRadius: '18px', background: '#eef5ff', border: `1px solid ${cores.borda}` }}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'flex-start',
                                                gap: '12px',
                                                marginBottom: '10px',
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <div>
                                                <div style={{ fontSize: '15px', fontWeight: 800, color: cores.textoEscuro }}>Modalidade</div>
                                                <div style={{ fontSize: '12px', color: cores.textoSuave, marginTop: '4px' }}>
                                                    {dados!.modalidade === 'EMPRESA' ? 'Volume por empresa' : 'Contabilidade interna'}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: '18px', fontWeight: 900, color: cores.azul }}>
                                                {formatarPercentual(kpi!.percentualConsumido)}
                                            </div>
                                        </div>

                                        {barraPercentual(kpi!.percentualConsumido, cores.azul)}

                                        <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr', gap: '8px' }}>
                                            <div style={{ fontSize: '12px', color: cores.textoMedio, fontWeight: 700 }}>
                                                Contratado: {formatarNumero(kpi!.quantidadeContratada)}
                                            </div>
                                            <div style={{ fontSize: '12px', color: cores.textoMedio, fontWeight: 700 }}>
                                                Utilizado: {formatarNumero(kpi!.quantidadeUtilizada)}
                                            </div>
                                            <div style={{ fontSize: '12px', color: cores.textoMedio, fontWeight: 700 }}>
                                                Disponível: {formatarNumero(kpi!.quantidadeRestante)}
                                            </div>
                                            {kpi!.quantidadeExcedente > 0 && (
                                                <div style={{ fontSize: '12px', color: cores.vermelho, fontWeight: 700 }}>
                                                    Excedente: {formatarNumero(kpi!.quantidadeExcedente)} ({formatarMoeda(kpi!.valorExcedente)})
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                        {blocoResumo('Itens', `${resumoLinhas.length}`, cores.textoEscuro, '#f8fafc')}
                                        {blocoResumo('Críticos', `${empresasCriticas}`, cores.vermelho, '#fff1f2')}
                                        {blocoResumo(
                                            'Top consumo',
                                            maiorConsumo ? formatarPercentual(maiorConsumo.percentualConsumido) : '0,0%',
                                            cores.laranjaEscuro,
                                            '#fff8eb'
                                        )}
                                        {blocoResumo('Média', formatarPercentual(mediaConsumoEmpresas), cores.azulEscuro, '#eef5ff')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DashboardContratosLancamentos;
